import { ApiClient, platformService } from '@monthio/react-components'
import {
	AutocompletedVehicle,
	VehicleLookupResponse,
	LookupAddressQuery,
	LookupAddressResult,
} from '#modules/AssetOverview/Tinglysning.types'

type SearchParamValues = string | number | boolean | null | undefined

export class TinglysningApiClient {
	static initialize(): TinglysningApiClient {
		const apiClient = platformService.createApiClient(
			platformService.getAppUrl('tinglysningApp'),
			{
				appendApi: true,
			},
		)
		return new TinglysningApiClient(apiClient)
	}

	constructor(private apiClient: ApiClient) {}

	/**
	 * Looks up vehicle information by registration number prefix
	 * @param registrationNumberPrefix The prefix to search for
	 * @returns Array of autocompleted vehicle information
	 */
	lookupVehicle = async (
		registrationNumberPrefix: string,
	): Promise<AutocompletedVehicle[]> => {
		try {
			const { vehicles } = await this.apiClient
				.retry(3)
				.get<VehicleLookupResponse>(
					this.getUriString(`vehicle/vehicle-lookup`, {
						licensePlatePrefix: registrationNumberPrefix,
					}),
				)
			return vehicles
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	/**
	 * Looks up address information based on query parameters
	 * @param query Address query parameters
	 * @returns Address lookup results
	 */
	lookupAddress = async (
		query: LookupAddressQuery,
	): Promise<LookupAddressResult> => {
		try {
			const addressResult = await this.apiClient.get(
				this.getUriString<LookupAddressQuery>('properties/addresses', query),
			)
			return addressResult
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	private getUriString<SearchParams extends { [key in keyof SearchParams]: SearchParamValues },
	>(location: string, params?: SearchParams): string {
		const searchParams = new URLSearchParams()

		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (typeof value === 'string') {
					// Filtering out empty strings
					if (!!value) {
						searchParams.append(key, value)
					}
					// Filtering out null and undefined values
				} else if (value !== undefined && value !== null) {
					searchParams.append(key, String(value))
				}
			})
		}

		const locationAlreadyHasSearch = location.indexOf('?') >= 0

		return (
			location +
			(searchParams.toString()
				? `${locationAlreadyHasSearch ? '&' : '?'}${searchParams.toString()}`
				: '')
		)
	}
}

export const tinglysningApiClient = TinglysningApiClient.initialize()
